import * as React from 'react';

import '@app/themes.css';
import '@patternfly/react-core/dist/styles/base.css';

import { BrowserRouter as Router } from 'react-router-dom';
import { AppLayout } from '@app/AppLayout/AppLayout';
import { AdminAlertPreviewProvider } from '@app/Admin/Alerts/AdminAlertPreviewContext';
import { AppRoutes } from '@app/routes';
import { MsalAuthenticationTemplate } from '@azure/msal-react';
import { InteractionType, AuthError } from '@azure/msal-browser';
import { PopupBlockerProvider } from '@app/components/PopupBlockerModal';
import { LegacyDomainAlert, isLegacyDomain } from '@app/components/LegacyDomainAlert';
import { PreviewJobStatusProvider } from '@app/PreviewEnv/context/PreviewJobStatusContext';
import { PreviewJobStatusWidget } from '@app/PreviewEnv/components/PreviewJobStatusWidget';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ENABLE_ALERTS } from './config';

import '@app/utilities.css';
import '@app/patternfly-overrides.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 2,
    },
  },
});

const LoadingComponent: React.FunctionComponent = () => (
  <div style={{ 
    display: 'flex', 
    justifyContent: 'center', 
    alignItems: 'center', 
    height: '100vh',
    flexDirection: 'column',
    gap: '1rem'
  }}>
    <div className="dx-spinner dx-spinner--lg" />
    <p>Redirecting to login...</p>
  </div>
);

const ErrorComponent: React.FunctionComponent<{ error: AuthError | null }> = ({ error }) => (
  <div style={{ 
    display: 'flex', 
    justifyContent: 'center', 
    alignItems: 'center', 
    height: '100vh',
    flexDirection: 'column',
    gap: '1rem',
    padding: '2rem'
  }}>
    <h2>Authentication Error</h2>
    <p>{error?.errorMessage || error?.message || 'An authentication error occurred'}</p>
    <p style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>
      Please refresh the page to try again.
    </p>
  </div>
);

const WithAlertsProvider: React.FunctionComponent<{ children: React.ReactNode }> = ({ children }) =>
  ENABLE_ALERTS ? (
    <AdminAlertPreviewProvider>{children}</AdminAlertPreviewProvider>
  ) : (
    <>{children}</>
  );

const App: React.FunctionComponent = () => {
  // Use Redirect for initial login (more reliable, user expects it)
  // Popup will be used for token refresh (preserves form state)
  return (
    <PopupBlockerProvider>
      <MsalAuthenticationTemplate
        interactionType={InteractionType.Redirect}
        authenticationRequest={{
          extraScopesToConsent: ['https://graph.microsoft.com/User.Read']
        }}
        loadingComponent={LoadingComponent}
        errorComponent={ErrorComponent}>
        {isLegacyDomain() ? (
          <LegacyDomainAlert />
        ) : (
          <QueryClientProvider client={queryClient}>
            <Router>
              <WithAlertsProvider>
                <PreviewJobStatusProvider>
                  <AppLayout>
                    <AppRoutes />
                  </AppLayout>
                  <PreviewJobStatusWidget />
                </PreviewJobStatusProvider>
              </WithAlertsProvider>
            </Router>
          </QueryClientProvider>
        )}
      </MsalAuthenticationTemplate>
    </PopupBlockerProvider>
  )
};

export default App;
